import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import { Drivers } from '@ionic/storage';

// Interfaces para mantener compatibilidad
export interface capDataStorageResult {
  result?: boolean;
}

export interface capDataStorageChanges {
  changes?: any;
}

export interface capStoreJson {
  export?: string;
}

@Injectable({
  providedIn: 'root'
})
export class StoreService {
  private storage: Storage | null = null;
  isService: boolean = false;
  platform: string = 'web';
  private currentDatabase: string = 'storage';
  private currentTable: string = 'storage_table';

  constructor(private ionicStorage: Storage) {
  }
    /**
   * Plugin Initialization
   */
  async init(): Promise<void> {
    this.platform = 'web'; // No necesitamos Capacitor.getPlatform() para storage

    // Inicializar Ionic Storage (configuración se hace en app.module.ts)
    this.storage = await this.ionicStorage.create();

    this.isService = true;
  }

  /**
   * Helper method para generar claves compuestas
   */
  private generateKey(key: string, database?: string, table?: string): string {
    const db = database || this.currentDatabase;
    const tbl = table || this.currentTable;
    return `${db}_${tbl}_${key}`;
  }

  /**
   * Helper method para extraer keys por prefijo
   */
  private async getKeysByPrefix(prefix: string): Promise<string[]> {
    if (!this.storage) return [];

    const allKeys = await this.storage.keys() || [];
    return allKeys.filter(key => key.startsWith(prefix));
  }

  /**
   * Echo a value
   * @param value
   */
   async echo(value: string): Promise<any> {
    if(this.isService && this.storage != null) {
        try {
            // Echo simplemente devuelve el valor recibido
            return { value };
        } catch (err) {
            console.log(`Error ${err}`)
            return Promise.reject(new Error(err.message));
        }
    } else {
        return Promise.reject(new Error("echo: Store not opened"));
    }
  }

  /**
   * Open a Store
   * @param _dbName string optional
   * @param _table string optional
   * @param _encrypted boolean optional
   * @param _mode string optional
   */
  async openStore(_dbName?:string,_table?:string,_encrypted?:boolean,_mode?:string): Promise<void> {
    if(this.isService && this.storage != null) {
      const database: string = _dbName ? _dbName : "storage";
      const table: string = _table ? _table : "storage_table";
      // Actualizar el contexto actual
      this.currentDatabase = database;
      this.currentTable = table;
      // Con Ionic Storage no necesitamos "abrir" explícitamente, solo configurar el contexto
      try {
        return Promise.resolve();
      } catch (err) {
        return Promise.reject(new Error(err.message));
      }
    } else {
      return Promise.reject(new Error("openStore: Store not opened"));
    }
  }
  /**
   * Close a store
   * @param dbName
   * @returns
   */
  async closeStore(dbName: String): Promise<void> {
    if(this.isService && this.storage != null) {
      try {
        // Con Ionic Storage no necesitamos cerrar explícitamente
        return Promise.resolve();
      } catch (err) {
        return Promise.reject(new Error(err.message));
      }
    } else {
      return Promise.reject(new Error("close: Store not opened"));
    }
  }
  /**
   * Check if a store is opened
   * @param dbName
   * @returns
   */
  async isStoreOpen(dbName: String): Promise<boolean> {
    if(this.isService && this.storage != null) {
      try {
        // Con Ionic Storage, si el servicio está inicializado, el store está "abierto"
        return Promise.resolve(true);
      } catch (err) {
        return Promise.reject(new Error(err.message));
      }
    } else {
      return Promise.reject(new Error("isStoreOpen: Store not opened"));
    }
  }
  /**
   * Check if a store already exists
   * @param dbName
   * @returns
   */
  async isStoreExists(dbName: String): Promise<boolean> {
    if(this.isService && this.storage != null) {
      try {
        // Verificar si existe alguna clave con el prefijo de esta database
        const prefix = `${dbName}_`;
        const keys = await this.getKeysByPrefix(prefix);
        return Promise.resolve(keys.length > 0);
      } catch (err) {
        return Promise.reject(new Error(err.message));
      }
    } else {
      return Promise.reject(new Error("isStoreExists: Store not opened"));
    }
  }
  /**
   * Create/Set a Table
   * @param table string
   */
  async setTable(table:string): Promise<void> {
    if(this.isService && this.storage != null) {
      try {
        // Simplemente actualizar el contexto de la tabla actual
        this.currentTable = table;
        return Promise.resolve();
      } catch (err) {
        return Promise.reject(new Error(err.message));
      }
    } else {
      return Promise.reject(new Error("setTable: Store not opened"));
    }
  }
  /**
   * Set of Key
   * @param key string
   * @param value string
   */
  async setItem(key:string,value:string): Promise<void> {
    if(this.isService && this.storage != null) {
      if(key.length > 0) {
        try {
          const compositeKey = this.generateKey(key);
          await this.storage.set(compositeKey, value);
          return Promise.resolve();
        } catch (err) {
          return Promise.reject(new Error(err.message));
        }
      } else {
        return Promise.reject(new Error("setItem: Must give a key"));
      }
    } else {
      return Promise.reject(new Error("setItem: Store not opened"));
    }
  }
  /**
   * Get the Value for a given Key
   * @param key string
   */
  async getItem(key:string): Promise<string> {
    if(this.isService && this.storage != null) {
      if(key.length > 0) {
        try {
          const compositeKey = this.generateKey(key);
          const value = await this.storage.get(compositeKey);
          // console.log("in getItem value ",value)
          return Promise.resolve(value);
        } catch (err) {
          console.log(`$$$$$ in getItem key: ${key} err: ${JSON.stringify(err)}`)
          return Promise.reject(new Error(err.message));
        }
      } else {
        return Promise.reject(new Error("getItem: Must give a key"));
      }
    } else {
      return Promise.reject(new Error("getItem: Store not opened"));
    }

  }
  async isKey(key:string): Promise<boolean> {
    if(this.isService && this.storage != null) {
      if(key.length > 0) {
        try {
          const compositeKey = this.generateKey(key);
          const value = await this.storage.get(compositeKey);
          return Promise.resolve(value !== null && value !== undefined);
        } catch (err) {
          return Promise.reject(new Error(err.message));
        }
      } else {
        return Promise.reject(new Error("isKey: Must give a key"));
      }
    } else {
      return Promise.reject(new Error("isKey: Store not opened"));
    }

  }

  async getAllKeys(): Promise<Array<string>> {
    if(this.isService && this.storage != null) {
      try {
        const prefix = this.generateKey('');
        const allKeys = await this.storage.keys() || [];
        const filteredKeys = allKeys
          .filter(key => key.startsWith(prefix))
          .map(key => key.replace(prefix, ''));
        return Promise.resolve(filteredKeys);
      } catch (err) {
        return Promise.reject(new Error(err.message));
      }
    } else {
      return Promise.reject(new Error("getAllKeys: Store not opened"));
    }
  }
  async getAllValues(): Promise<Array<string>> {
    if(this.isService && this.storage != null) {
      try {
        const keys = await this.getAllKeys();
        const values: string[] = [];
        for (const key of keys) {
          const value = await this.getItem(key);
          if (value !== null && value !== undefined) {
            values.push(value);
          }
        }
        return Promise.resolve(values);
      } catch (err) {
        return Promise.reject(new Error(err.message));
      }
    } else {
      return Promise.reject(new Error("getAllValues: Store not opened"));
    }
  }
  async getFilterValues(filter:string): Promise<Array<string>> {
    if(this.isService && this.storage != null) {
      try {
        const allValues = await this.getAllValues();
        const filteredValues = allValues.filter(value =>
          value && value.toString().includes(filter)
        );
        return Promise.resolve(filteredValues);
      } catch (err) {
        return Promise.reject(new Error(err.message));
      }
    } else {
      return Promise.reject(new Error("getFilterValues: Store not opened"));
    }
  }
  async getAllKeysValues(): Promise<Array<any>> {
    if(this.isService && this.storage != null) {
      try {
        const keys = await this.getAllKeys();
        const keysvalues: any[] = [];
        for (const key of keys) {
          const value = await this.getItem(key);
          if (value !== null && value !== undefined) {
            keysvalues.push({ key, value });
          }
        }
        return Promise.resolve(keysvalues);
      } catch (err) {
        return Promise.reject(new Error(err.message));
      }
    } else {
      return Promise.reject(new Error("getAllKeysValues: Store not opened"));
    }
  }

  async removeItem(key:string): Promise<void> {
    if(this.isService && this.storage != null) {
      if(key.length > 0) {
        try {
          const compositeKey = this.generateKey(key);
          await this.storage.remove(compositeKey);
          return Promise.resolve();
        } catch (err) {
          return Promise.reject(new Error(err.message));
        }
      } else {
        return Promise.reject(new Error("removeItem: Must give a key"));
      }
    } else {
      return Promise.reject(new Error("removeItem: Store not opened"));
    }
  }

  async clear(): Promise<void> {
    if(this.isService && this.storage != null) {
      try {
        // Limpiar solo las claves de la tabla/database actual
        const keys = await this.getAllKeys();
        for (const key of keys) {
          await this.removeItem(key);
        }
        return Promise.resolve();
      } catch (err) {
          return Promise.reject(new Error(err.message));
        }
    } else {
      return Promise.reject(new Error("clear: Store not opened"));
    }
  }

  async deleteStore(_dbName?:string): Promise<void> {
    const database: string = _dbName ? _dbName : "storage";
    await this.init();
    if(this.isService && this.storage != null) {
      try {
        // Eliminar todas las claves que empiecen con el prefijo de la database
        const prefix = `${database}_`;
        const allKeys = await this.storage.keys() || [];
        const keysToDelete = allKeys.filter(key => key.startsWith(prefix));

        for (const key of keysToDelete) {
          await this.storage.remove(key);
        }
        return Promise.resolve();
      } catch (err) {
          return Promise.reject(new Error(err.message));
      }
    } else {
      return Promise.reject(new Error("deleteStore: Store not opened"));
    }
  }
  async isTable(table:string): Promise<boolean> {
    if(this.isService && this.storage != null) {
      if(table.length > 0) {
        try {
          // Verificar si existe alguna clave con el prefijo de esta tabla
          const prefix = `${this.currentDatabase}_${table}_`;
          const keys = await this.getKeysByPrefix(prefix);
          return Promise.resolve(keys.length > 0);
        } catch (err) {
          return Promise.reject(new Error(err.message));
        }
      } else {
        return Promise.reject(new Error("isTable: Must give a table"));
      }
    } else {
      return Promise.reject(new Error("isTable: Store not opened"));
    }
  }
  async getAllTables(): Promise<Array<string>> {
    if(this.isService && this.storage != null) {
      try {
        const prefix = `${this.currentDatabase}_`;
        const allKeys = await this.storage.keys() || [];
        const tablesSet = new Set<string>();

        allKeys
          .filter(key => key.startsWith(prefix))
          .forEach(key => {
            const parts = key.split('_');
            if (parts.length >= 3) {
              tablesSet.add(parts[1]); // segunda parte es la tabla
            }
          });

        return Promise.resolve(Array.from(tablesSet));
      } catch (err) {
        return Promise.reject(new Error(err.message));
      }
    } else {
      return Promise.reject(new Error("getAllTables: Store not opened"));
    }
  }
  async deleteTable(table?:string): Promise<void> {
    if(this.isService && this.storage != null) {
      if(table && table.length > 0) {
        try {
          // Eliminar todas las claves que empiecen con el prefijo de la tabla
          const prefix = `${this.currentDatabase}_${table}_`;
          const keys = await this.getKeysByPrefix(prefix);

          for (const key of keys) {
            await this.storage.remove(key);
          }
          return Promise.resolve();
        } catch (err) {
            return Promise.reject(new Error(err.message));
        }
      } else {
        return Promise.reject(new Error("deleteTable: Must give a table"));
      }
    } else {
      return Promise.reject(new Error("deleteTable: Store not opened"));
    }
  }
  /**
   * Is Json Object Valid
   * @param jsonstring Check the validity of a given Json Object
   */

  async isJsonValid(jsonstring:string): Promise<capDataStorageResult> {
    if(this.isService && this.storage != null) {
      try {
          JSON.parse(jsonstring);
          return Promise.resolve({ result: true });
      } catch (err) {
          return Promise.resolve({ result: false });
      }
    } else {
        return Promise.reject(new Error(`no store open`));
    }

  }
  /**
   * Import from a Json Object
   * @param jsonstring
   */
  async importFromJson(jsonstring:string): Promise<capDataStorageChanges> {
    if(this.isService && this.storage != null) {
      try {
            const data = JSON.parse(jsonstring);
            let changes = 0;

            // Si el JSON tiene la estructura esperada con claves y valores
            if (typeof data === 'object' && data !== null) {
              for (const [key, value] of Object.entries(data)) {
                await this.setItem(key, String(value));
                changes++;
              }
            }

            return Promise.resolve({ changes });
        } catch (err) {
            return Promise.reject(new Error(err.message));
        }
    } else {
        return Promise.reject(new Error(`no store open`));
    }

  }
  /**
   * Export to a Json Object
   */
  async exportToJson(): Promise<capStoreJson> {
    if(this.isService && this.storage != null) {
      try {
            const keysValues = await this.getAllKeysValues();
            const exportObj: any = {};

            keysValues.forEach(item => {
              exportObj[item.key] = item.value;
            });

            return Promise.resolve({ export: JSON.stringify(exportObj) });
        } catch (err) {
            return Promise.reject(new Error(err.message));
        }
    } else {
        return Promise.reject(new Error(`no store open`));
    }

  }

}
